import {
  ApiErrorInstance,
  CustomDomainStatuses,
  deleteClientSecret,
  EnvironmentData,
  getClientCredentialsKey,
  getEnvironmentMultiApplicationDetailQueryKey,
  isApi5xxResponseStatus,
  withHandleError
} from '~/api';
import logger from '~/utils/logger';
import { getLocalizedString } from '~/i18n/utils.ts';
import { ConfirmModalValues } from '~/components/ui/Modal/ConfirmModalContext.tsx';
import { ToastContextValue } from '~/components/ui/Toast';
import { QueryClient } from '@tanstack/solid-query';
import { getTranslationKeyFunction } from '~/utils/commonUtils.ts';
import { HttpStatusCode } from 'axios';

enum SettingsTabTypes {
  AUTH_MODE = 'authentication-mode',
  API_CREDENTIALS = 'api-credentials',
  APPLICATION_DETAILS = 'application-details'
}

export enum ClientSecretFlowEnum {
  MULTI_APPLICATION = 'MULTI_APPLICATION'
}

const TRANSLATION_BASE_PATH = `pages.settings`;

const getTranslationKey = (translationStr: string): string =>
  `${TRANSLATION_BASE_PATH}.${translationStr}`;

const getApiCredentialsTranslationKey = getTranslationKeyFunction(
  `${TRANSLATION_BASE_PATH}.api_credentials`
);

const getApplicationDetailsTranslationKey = getTranslationKeyFunction(
  `${TRANSLATION_BASE_PATH}.application_details`
);

function getEnvironmentDomain(env: EnvironmentData) {
  return (
    (env.customDomainStatus === CustomDomainStatuses.ACTIVE &&
      env.customDomain) ||
    env.domain
  );
}

function getEnvironmentUrl(env: EnvironmentData) {
  return `https://${getEnvironmentDomain(env)}`;
}

type InputFieldConfig = {
  id: string;
  name: string;
  label: string;
  placeholder: string;
  hint?: string;
};

const getApplicationInputFieldConfig = ({
  intl
}: {
  intl: any;
}): InputFieldConfig[] => [
  {
    id: 'application_name',
    name: 'name',
    label: getLocalizedString(
      getApplicationDetailsTranslationKey('application_name_label'),
      { intl }
    ),
    placeholder: getLocalizedString(
      getApplicationDetailsTranslationKey('application_name_placeholder'),
      { intl }
    ),
    hint: getLocalizedString(
      getApplicationDetailsTranslationKey('application_name_hint'),
      { intl }
    )
  },
  {
    id: 'application_support_email',
    name: 'supportEmail',
    label: getLocalizedString(
      getApplicationDetailsTranslationKey('application_support_email_label'),
      { intl }
    ),
    placeholder: getLocalizedString(
      getApplicationDetailsTranslationKey(
        'application_support_email_placeholder'
      ),
      { intl }
    ),
    hint: getLocalizedString(
      getApplicationDetailsTranslationKey('application_support_email_hint'),
      { intl }
    )
  },
  {
    id: 'application_privacy_policy_url',
    name: 'privacyPolicyUrl',
    label: getLocalizedString(
      getApplicationDetailsTranslationKey(
        'application_privacy_policy_url_label'
      ),
      { intl }
    ),
    placeholder: getLocalizedString(
      getApplicationDetailsTranslationKey(
        'application_privacy_policy_url_placeholder'
      ),
      { intl }
    ),
    hint: getLocalizedString(
      getApplicationDetailsTranslationKey(
        'application_privacy_policy_url_hint'
      ),
      { intl }
    )
  },
  {
    id: 'application_tos_url',
    name: 'termsOfServiceUrl',
    label: getLocalizedString(
      getApplicationDetailsTranslationKey(
        'application_terms_of_service_url_label'
      ),
      { intl }
    ),
    placeholder: getLocalizedString(
      getApplicationDetailsTranslationKey(
        'application_terms_of_service_url_placeholder'
      ),
      { intl }
    ),
    hint: getLocalizedString(
      getApplicationDetailsTranslationKey(
        'application_terms_of_service_url_hint'
      ),
      { intl }
    )
  },
  {
    id: 'organization_meta_name',
    name: 'organizationMetaName',
    label: getLocalizedString(
      getApplicationDetailsTranslationKey('header_organization_meta_name'),
      { intl }
    ),
    placeholder: getLocalizedString(
      getApplicationDetailsTranslationKey('organization_meta_name_placeholder'),
      { intl }
    ),
    hint: getLocalizedString(
      getApplicationDetailsTranslationKey('subheader_organization_meta_name'),
      { intl }
    )
  }
];

const deleteClientSecretWithConfirmation = ({
  clientId,
  secretId,
  confirm,
  toaster,
  queryClient,
  intl,
  flowType
}: {
  clientId: string;
  secretId: string;
  applicationId?: string;
  confirm: ConfirmModalValues;
  toaster: ToastContextValue;
  queryClient: QueryClient;
  intl: any;
  flowType?: ClientSecretFlowEnum;
}) => {
  confirm.show({
    title: getLocalizedString(
      getApiCredentialsTranslationKey('delete_client_secret_confirm_title'),
      { intl }
    ),
    description: getLocalizedString(
      getApiCredentialsTranslationKey('delete_client_secret_confirm_message'),
      { intl }
    ),
    primaryActionLabel: getLocalizedString('cta_labels.delete', { intl }),
    primaryActionDataTestId: 'settings-client-secret-delete',
    onConfirm: async () => {
      try {
        confirm.setIsLoading(true);
        await deleteClientSecret(clientId, secretId);
        await queryClient.refetchQueries({
          queryKey: getQueryKeysForRefetching({
            flowType,
            applicationIdForMultiApp: clientId
          }),
          type: 'active',
          exact: true
        });
        confirm.setIsLoading(false);
        confirm.close();
        toaster.toast().success({
          description: getLocalizedString(
            getApiCredentialsTranslationKey(
              'delete_client_secret_success_toast'
            ),
            { intl }
          )
        });
      } catch (error) {
        confirm.setIsLoading(false);
        confirm.close();
        logger.error(error);
        toaster.toast().error({
          description: getLocalizedString(
            getApiCredentialsTranslationKey(
              'delete_client_secret_failure_toast'
            ),
            { intl }
          )
        });
      }
    }
  });
};

const errorTranslationBasePath = 'server_errors.customizations';

const handleUpdateCustomizationError = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (
    serverError.httpStatus === HttpStatusCode.BadRequest ||
    isApi5xxResponseStatus(serverError.httpStatus)
  ) {
    return getLocalizedString(
      `${errorTranslationBasePath}.application_details_update_failed`,
      {
        intl
      }
    );
  }
});

const getAppTitle = ({
  intl,
  tabType
}: {
  intl: any;
  tabType: SettingsTabTypes;
}) => {
  const titleMap: Record<SettingsTabTypes, string> = {
    [SettingsTabTypes.API_CREDENTIALS]: getLocalizedString(
      'page_titles.api_credentials',
      { intl }
    ),
    [SettingsTabTypes.APPLICATION_DETAILS]: getLocalizedString(
      'page_titles.application_details',
      { intl }
    ),
    [SettingsTabTypes.AUTH_MODE]: getLocalizedString(
      'page_titles.authentication_mode',
      { intl }
    )
  };

  return (
    (tabType && titleMap?.[tabType]) ||
    getLocalizedString('page_titles.settings', { intl })
  );
};

const getQueryKeysForRefetching = ({
  flowType,
  applicationIdForMultiApp
}: {
  flowType?: ClientSecretFlowEnum;
  applicationIdForMultiApp?: string;
}) => {
  switch (flowType) {
    case ClientSecretFlowEnum.MULTI_APPLICATION:
      return getEnvironmentMultiApplicationDetailQueryKey(
        applicationIdForMultiApp ?? ''
      );
    default:
      return getClientCredentialsKey();
  }
};

export {
  SettingsTabTypes,
  getApplicationInputFieldConfig,
  getTranslationKey,
  getApiCredentialsTranslationKey,
  getApplicationDetailsTranslationKey,
  getEnvironmentUrl,
  deleteClientSecretWithConfirmation,
  handleUpdateCustomizationError,
  getQueryKeysForRefetching,
  getAppTitle
};
